import React from 'react';
import PropTypes from 'prop-types';
import { isMobile } from 'react-device-detect';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { withStyles, Paper } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import CircularProgress from 'components/shared/CircularProgress';
import { hasReachableCounterpart } from '../conversationLock';
import Messages, { StugknutenMessage } from './Messages';
import Footer from './Footer';

const styles = (theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 'auto',
    overflow: 'hidden',
    boxShadow: '0px -1px 2px -1px rgba(0, 0, 0, 0.2)',
  },
  paper: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  chat: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(2),
  },
  noteMsg: {
    backgroundColor: '#C5E1A5',
    borderRadius: 4,
    fontSize: 12,
    margin: '0 auto',
    maxWidth: 580,
    padding: theme.spacing(2),
    textAlign: 'center',
  },
});

const top = 112;

const cssHeight = (value = '100vh') =>
  typeof value === 'number' ? value - top : `calc(${value} - ${top}px)`;

class MessageTab extends React.Component {
  state = {};

  inputRef = React.createRef();

  formRef = React.createRef();

  componentDidMount() {
    if ('visualViewport' in window) {
      window.visualViewport.addEventListener('resize', this.resizeEvent);
    }
    window.addEventListener('resize', this.resizeEvent);
    window.addEventListener('scroll', this.scrollEvent);
    window.addEventListener('touchstart', this.clickOutside);

    this.calcHeight();

    if (this.props.data && this.props.data.Messages) {
      setTimeout(() => {
        this.scrollChat();
      }, 50);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { height } = this.state;

    if (!this.props.showLoader && prevProps.data !== this.props.data) {
      this.scrollChat();
    }

    if (prevState.height !== height && window.pageYOffset !== 0) {
      window.scrollTo(0, 0);
    }
  }

  componentWillUnmount() {
    if ('visualViewport' in window) {
      window.visualViewport.removeEventListener('resize', this.resizeEvent);
    }
    window.removeEventListener('resize', this.resizeEvent);
    window.removeEventListener('scroll', this.scrollEvent);
    window.removeEventListener('touchstart', this.clickOutside);

    enableBodyScroll(this.inputRef.current);

    clearTimeout(this.resizeTimeout);
    clearTimeout(this.blurTimeout);
  }

  onFocus = () => {
    disableBodyScroll(this.inputRef.current);
    this.setState({ focus: true });
    this.calcHeight();
  };

  onBlur = () => {
    enableBodyScroll(this.inputRef.current);
    this.setState({ focus: false });
    this.blurTimeout = setTimeout(this.calcHeight, 64); // Fix for iOS not sending msg bug
  };

  clickOutside = (event) => {
    const { current } = this.formRef;
    const { target } = event;

    if (current && !current.contains(target)) {
      this.inputRef.current.blur();
    }
  };

  setChatRef = (c) => {
    this.chatRef = c;
  };

  calcHeight = (isScroll) => {
    let msgInputFix = 0;
    if (isMobile && window.innerWidth > 320) {
      msgInputFix = 20;
    }

    const { innerHeight } = window;
    const height = this.state.focus ? cssHeight(innerHeight) - msgInputFix : cssHeight(innerHeight);
    const now = +new Date();

    // Mobile landscape input detection
    // const isLandscape = window.matchMedia('(orientation: landscape)').matches;
    // if (isMobile && isLandscape) {
    //  return;
    // }

    // Prevent setting wrong height on iOS/iPhone with opened keyboard
    // iOS will force scroll and change window height twice (last time with wrong value)
    // thats why we need to check this.lastTime to skip second value
    if (!isScroll || !this.lastTime || height < this.state.height || now > this.lastTime + 512) {
      this.setState({ height });
      this.lastTime = now;
    }
  };

  resizeEvent = (event, isScroll) => {
    if (!this.resizeTimeout) {
      this.resizeTimeout = setTimeout(() => {
        this.resizeTimeout = null;
        this.calcHeight(isScroll);
      }, 64);
    }
  };

  scrollEvent = (event) => {
    this.resizeEvent(event, true);
  };

  scrollChat = () => {
    if (this.chatRef) {
      this.chatRef.scrollTop = this.chatRef.scrollHeight;
    }
  };

  render() {
    const {
      classes,
      currentUser,
      data,
      showNoteMessage,
      showLoader,
      hasError,
      deleteMessage,
      conversationId,
      isHost,
    } = this.props;

    const { height } = this.state;
    // The backend decides whether the conversation is locked.
    const lockReason = data?.lock_reason ?? null;
    // Don't allow sending until the conversation is loaded and has someone to
    // reach -- otherwise a message could be posted into an unloaded or
    // counterpart-less conversation (no banner; that's reserved for the lock).
    const footerDisabled =
      !data || !data.Messages || !hasReachableCounterpart(data.Users, currentUser?.id);

    let chatBody;
    if (showLoader) {
      chatBody = <CircularProgress />;
    } else if (hasError) {
      // Missing conversation or no access -- show a message instead of spinning.
      chatBody = (
        <StugknutenMessage>
          <FormattedMessage
            id="conversations.loadError"
            defaultMessage="This conversation could not be found, or you no longer have access to it."
          />
        </StugknutenMessage>
      );
    } else {
      chatBody = (
        <>
          {showNoteMessage && (
            <div>
              <StugknutenMessage>
                <FormattedMessage
                  id="conversations.sCertifiedNote"
                  defaultMessage="This accommodation is S-certified. Please remember
                      that you must make payment via Stugknuten's platform in order
                      to qualify for the protection provided by S-certification."
                />
              </StugknutenMessage>
            </div>
          )}
          <Messages
            data={data}
            currentUserId={currentUser.id}
            deleteMessage={deleteMessage}
            isHost={isHost}
          />
        </>
      );
    }

    return (
      <div className={classes.root} style={{ height }}>
        <Paper className={classes.paper}>
          <div ref={this.setChatRef} className={classes.chat}>
            {chatBody}
          </div>
        </Paper>
        <Footer
          isShowContactsWarning={
            data?.filters?.commission &&
            data?.filters?.conversation_bookings === 0 &&
            data?.filters?.total_host_bookings_18m < 2
          }
          isShowTerminationWarning={isHost}
          isHost={isHost}
          disableFileInput={data?.filters?.files}
          cottageId={data?.cottage_id}
          setInputRef={this.inputRef}
          setFormRef={this.formRef}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
          conversationId={conversationId}
          lockReason={lockReason}
          disabled={footerDisabled}
        />
      </div>
    );
  }
}

MessageTab.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  currentUser: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
  data: PropTypes.shape({
    Messages: PropTypes.arrayOf(PropTypes.shape({})),
    Users: PropTypes.arrayOf(PropTypes.shape({})),
    lock_reason: PropTypes.string,
    cottage_id: PropTypes.number,
    filters: PropTypes.shape({
      emails: PropTypes.bool,
      phone_numbers: PropTypes.bool,
      files: PropTypes.bool,
      links: PropTypes.bool,
      commission: PropTypes.bool,
      bank_information: PropTypes.bool,
      conversation_bookings: PropTypes.number,
      total_host_bookings_18m: PropTypes.number,
      termination_warning: PropTypes.bool,
    }),
  }),
  conversationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showLoader: PropTypes.bool,
  hasError: PropTypes.bool,
  showNoteMessage: PropTypes.bool,
  isHost: PropTypes.bool.isRequired,

  deleteMessage: PropTypes.func.isRequired,
};

export default withStyles(styles)(MessageTab);
